import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Briefcase, ArrowRight, Lock, Sparkles, Clock } from "lucide-react";
import { jobs, companyName, fetchPublicJobs, type Job } from "@/components/site/jobs";
import { allEmployerJobs, type EmployerJob } from "@/components/site/employer";
import { demoJobs, demoFunnelJob } from "@/components/site/admin-demo";
import { Nav } from "@/components/hirekit/Nav";
import {
  type Invite,
  fetchInviteByToken,
  formatCountdown,
  inviteTimeLeft,
  trackInviteApi,
} from "@/components/site/admin";
import { SeoFooter } from "@/components/site/SeoFooter";

/** Roles CLOSDD can always resolve, even on a device with no local store. */
const catalog: EmployerJob[] = [demoFunnelJob, ...demoJobs];

const toJob = (j: EmployerJob): Job => ({
  id: j.id,
  title: j.title,
  seniority: j.seniority,
  contract: j.contract,
  company: j.company,
  anonymous: j.anonymous,
  location: j.location,
  setting: j.setting,
  payStructure: j.payStructure,
  pay: j.pay,
  advert: j.advert,
  posted: "Invitation only",
  logoTone: "bg-purple-soft",
  tags: j.tags,
});

const matches = (key: string, id: string, ref?: string) => {
  const k = key.trim().toLowerCase();
  return id.toLowerCase() === k || (ref ?? "").toLowerCase() === k;
};

function resolveJob(key: string): Job | null {
  const staticMatch = jobs.find((j) => matches(key, j.id));
  if (staticMatch) return staticMatch;
  const seeded = catalog.find((j) => matches(key, j.id, j.ref));
  return seeded ? toJob(seeded) : null;
}

export const Route = createFileRoute("/invite/$jobId")({
  validateSearch: (search: Record<string, unknown>): { name: string; email: string } => ({
    name: typeof search["name"] === "string" ? (search["name"] as string) : "",
    email: typeof search["email"] === "string" ? (search["email"] as string) : "",
  }),
  loader: ({ params }) => {
    return { job: resolveJob(params.jobId), jobId: params.jobId };
  },
  head: ({ loaderData }) => {
    if (!loaderData?.job) {
      return { meta: [{ title: "Invitation | CLOSDD" }, { name: "robots", content: "noindex" }] };
    }
    const t = `You have been shortlisted for ${loaderData.job.title} | CLOSDD`;
    const d = `CLOSDD shortlisted you for ${loaderData.job.title}. Read the role, then create your free account and apply with a 60-second pitch.`;
    return {
      meta: [
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "noindex" },
      ],
    };
  },
  component: InvitePage,
});

const cta =
  "group inline-flex w-full items-center justify-center gap-2 rounded-full bg-purple-strong px-8 py-4 text-[14px] font-bold uppercase tracking-[0.12em] text-white shadow-xl shadow-purple-strong/18 transition hover:-translate-y-0.5 hover:bg-purple-mid sm:w-auto";
const ctaLight =
  "inline-flex items-center rounded-full border border-ink/15 bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-ink/70 transition hover:border-ink/35 hover:text-ink";

/**
 * Personalised welcome page for a token invitation. Deliberately bare: no job
 * board navigation, no search, no other roles.
 */
/** Live countdown banner that drives urgency on the invitation. */
function ExpiryBanner({ invite }: { invite: Invite }) {
  const [left, setLeft] = useState<number | null>(() => inviteTimeLeft(invite));

  useEffect(() => {
    if (!invite.expiresAt) return;
    const tick = () => setLeft(inviteTimeLeft(invite));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [invite]);

  if (left === null) return null;

  if (left <= 0) {
    return (
      <div className="mt-5 rounded-[22px] border border-ink/10 bg-ink/[0.03] px-6 py-5 text-center">
        <p className="text-[13px] font-semibold text-ink/60">
          This invitation&apos;s priority window has closed — you can still apply, but you&apos;ll join the standard
          queue.
        </p>
      </div>
    );
  }

  const { h, m, s } = formatCountdown(left);
  const unit = (v: string, l: string) => (
    <div className="flex flex-col items-center">
      <span className="font-serif text-[22px] leading-none tracking-[-0.02em] text-white sm:text-[28px]">{v}</span>
      <span className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">{l}</span>
    </div>
  );

  return (
    <div className="mt-5 overflow-hidden rounded-[24px] border border-tag-red/25 bg-ink px-6 py-6 text-white shadow-lg sm:px-8">
      <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:justify-between sm:text-left">
        <div className="max-w-[420px]">
          <div className="flex items-center justify-center gap-2 sm:justify-start">
            <Clock className="h-3.5 w-3.5 text-tag-red" />
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-tag-red">Time-limited invitation</p>
          </div>
          <p className="mt-2.5 text-[15px] font-semibold leading-[1.5]">
            This exclusive invitation expires soon.
          </p>
          <p className="mt-1.5 text-[13px] leading-[1.55] text-white/60">
            Apply before the timer ends and your application is shortlisted first — sent straight to the top of the
            employer&apos;s list.
          </p>
        </div>
        <div className="flex items-start gap-4 sm:gap-5">
          {unit(h, "Hours")}
          <span className="font-serif text-[20px] leading-none text-white/25 sm:text-[25px]">:</span>
          {unit(m, "Mins")}
          <span className="font-serif text-[20px] leading-none text-white/25 sm:text-[25px]">:</span>
          {unit(s, "Secs")}
        </div>
      </div>
    </div>
  );
}

function TokenInvite({ invite, job }: { invite: Invite; job: Job | null }) {
  useEffect(() => {
    void trackInviteApi(invite.token ?? "", "Opened");
  }, [invite.token]);

  if (invite.status === "Cancelled" || invite.status === "Expired") {
    return (
      <div className="cl min-h-screen bg-cream">
        <main className="mx-auto w-full max-w-[640px] px-5 py-24 text-center">
          <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-purple-strong">CLOSDD</p>
          <h1 className="mt-6 font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">
            This invitation is no longer active.
          </h1>
          <p className="mt-4 text-[17px] text-ink/65">Email info@closdd.com and we will take another look for you.</p>
        </main>
      </div>
    );
  }

  const first = invite.firstName?.trim() || invite.name?.trim().split(" ")[0] || "";
  const company = invite.company?.trim() || job?.company?.trim() || "our client";
  const title = invite.jobTitle?.trim() || job?.title?.trim() || "this role";
  const location = invite.location?.trim() || job?.location?.trim() || "";
  const pay = invite.pay?.trim() || job?.pay?.trim() || "";
  const meta = [company, location, pay].filter(Boolean);

  const steps = [
    {
      n: "01",
      h: "View the role",
      p: "Take a look at the opportunity and what the employer is looking for.",
    },
    {
      n: "02",
      h: "Submit your CLOSDD profile",
      p: "Upload your CV, record your 60-second pitch and complete your application.",
    },
    {
      n: "03",
      h: "We'll forward your application",
      p: "Once completed, we send your profile directly to the hiring team.",
    },
  ];

  return (
    <div className="cl min-h-screen bg-cream">
      <main className="mx-auto w-full max-w-[760px] px-5 py-10 sm:py-16">
        {/* Top invitation label */}
        <div className="flex items-center justify-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tag-green/60 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-tag-green" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-purple-strong">
            CLOSDD Candidate Invitation
          </span>
        </div>

        {/* Hero card */}
        <div className="mt-8 overflow-hidden rounded-[32px] bg-purple-strong text-white shadow-2xl shadow-purple-strong/20">
          <div className="relative px-7 pb-10 pt-12 sm:px-12 sm:pb-12 sm:pt-16">
            <div className="pointer-events-none absolute -right-16 -top-20 h-[280px] w-[280px] rounded-full bg-white/10 blur-[100px]" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-[260px] w-[260px] rounded-full bg-purple-mid/30 blur-[90px]" />

            <div className="relative">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-white/60" />
                <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-white/60">Selected for you</p>
              </div>

              {first ? (
                <h1 className="mt-5 font-serif text-[34px] leading-[0.95] tracking-[-0.03em] sm:text-[48px]">{first}</h1>
              ) : (
                <h1 className="mt-5 max-w-[620px] font-serif text-[28px] leading-[1] tracking-[-0.03em] sm:text-[40px]">
                  We&apos;d like to put you forward.
                </h1>
              )}

              <p className="mt-7 max-w-[560px] text-[16px] leading-[1.6] text-white/80">
                We&apos;d like to put you forward for the{" "}
                <span className="font-semibold text-white">{title}</span> role at{" "}
                <span className="font-semibold text-white">{company}</span>.
              </p>
            </div>
          </div>

          {/* Premium job card */}
          <div className="relative border-t border-white/10 bg-white/[0.06] px-7 py-7 backdrop-blur-sm sm:px-12 sm:py-8">
            <div className="flex items-start gap-5">
              <div className="hidden h-14 w-14 shrink-0 rounded-2xl bg-white/10 sm:grid sm:place-items-center">
                <Briefcase className="h-6 w-6 text-white/90" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">Opportunity</p>
                <h2 className="mt-1.5 font-serif text-[19px] leading-[1.15] tracking-[-0.02em] text-white sm:text-[22px]">
                  {title}
                </h2>
                {meta.length ? (
                  <p className="mt-2.5 truncate text-[14px] font-medium text-white/60">{meta.join(" · ")}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <ExpiryBanner invite={invite} />

        {/* Personal message */}
        {invite.message?.trim() ? (
          <div className="mt-5 rounded-[24px] border border-purple-strong/12 bg-purple-soft px-7 py-6 sm:px-8">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-strong">Why we reached out</p>
            <p className="mt-3 whitespace-pre-line text-[16px] leading-[1.65] text-ink/75">{invite.message.trim()}</p>
          </div>
        ) : null}

        {/* Process section */}
        <section className="mt-12">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink/45">Simple process</p>
              <h2 className="mt-1 font-serif text-[19px] leading-[1.1] tracking-[-0.02em] text-ink sm:text-[21px]">
                What happens next
              </h2>
            </div>
            <span className="hidden text-[11px] font-bold uppercase tracking-[0.16em] text-ink/40 sm:block">
              3 steps
            </span>
          </div>

          <ol className="mt-6 grid gap-4 sm:grid-cols-3">
            {steps.map((s, i) => (
              <li
                key={s.n}
                className="relative rounded-[22px] border border-ink/8 bg-white/70 px-6 py-6 sm:px-5 sm:py-6"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-strong text-[13px] font-bold text-white">
                  {i + 1}
                </span>
                <p className="mt-4 text-[16px] font-semibold leading-[1.35] text-ink">{s.h}</p>
                <p className="mt-2 text-[14px] leading-[1.55] text-ink/55">{s.p}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Dominant CTA */}
        <div className="mt-10 rounded-[28px] border border-ink/10 bg-white px-6 py-9 text-center shadow-sm sm:px-10 sm:py-10">
          <p className="text-[15px] font-semibold text-ink">Ready to take a closer look?</p>
          <p className="mt-1 text-[14px] text-ink/55">View the full role and apply in minutes.</p>
          <Link
            to="/jobs/$jobId"
            params={{ jobId: invite.jobId }}
            search={{ inv: invite.token ?? "" }}
            className={`${cta} mt-6`}
          >
            View role &amp; apply
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <p className="mt-4 text-[12px] text-ink/45">
            No CV required to view the role. Apply before your invitation expires to be shortlisted first.
          </p>
        </div>

        {/* Why you're receiving this */}
        <div className="mt-5 flex items-start gap-3.5 rounded-[20px] border border-ink/8 bg-ink/[0.02] px-5 py-4">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ink/40" />
          <p className="text-[13px] leading-[1.55] text-ink/55">
            You&apos;re receiving this because a CLOSDD talent partner shortlisted your profile for this specific
            opportunity. This link is unique to you.
          </p>
        </div>

        <div className="mt-10">
          <SeoFooter />
        </div>
      </main>
    </div>
  );
}

function InvitePage() {
  const { job: staticJob, jobId } = Route.useLoaderData();
  const { name, email } = Route.useSearch();
  const [job, setJob] = useState<Job | null>(staticJob);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchInviteByToken(jobId)
      .then((found) => {
        if (!cancelled) setInvite(found);
      })
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (staticJob) return;
    const found = allEmployerJobs().find((j) => matches(jobId, j.id, j.ref));
    if (found) {
      setJob(toJob(found));
      return;
    }
    fetchPublicJobs()
      .then((live) => {
        const match = live.find((j) => matches(jobId, j.id));
        if (match) setJob(match);
      })
      .catch(() => {});
  }, [staticJob, jobId]);

  if (!checked) return <div className="cl min-h-screen bg-cream" />;
  if (invite) return <TokenInvite invite={invite} job={job ?? resolveJob(invite.jobId)} />;

  const first = name.trim() ? name.trim().split(" ")[0] : "";

  if (!job) {
    return (
      <div className="cl min-h-screen bg-cream">
        <Nav />
        <main className="mx-auto w-full max-w-[720px] px-5 py-24 text-center">
          <h1 className="font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">
            This invitation is no longer live.
          </h1>
          <p className="mt-4 text-[17px] text-ink/65">
            The role may have been filled. Browse every sales role on CLOSDD instead.
          </p>
          <Link to="/find-jobs" className={`${ctaLight} mt-8`}>
            Find jobs
          </Link>
        </main>
      </div>
    );
  }

  const applySearch = { invite: email || "" };

  return (
    <div className="cl min-h-screen bg-cream">
      <Nav />
      <main className="mx-auto w-full max-w-[1120px] px-5 pb-20 pt-8">
        <section className="relative overflow-hidden rounded-[32px] bg-purple-strong px-7 py-12 text-white sm:px-12 sm:py-16">
          <div className="pointer-events-none absolute -left-32 -top-40 h-[420px] w-[420px] rounded-full bg-white/10 blur-[120px]" />
          <div className="relative">
            <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-white/60">
              Shortlisted · invitation to apply
            </p>
            <h1 className="mt-6 max-w-[860px] font-serif text-[28px] leading-[1.02] tracking-[-0.03em] sm:text-[38px]">
              {first ? `${first}, you` : "You"} have been shortlisted to apply for {job.title}.
            </h1>
            <p className="mt-6 max-w-[680px] text-[16px] leading-[1.65] text-white/70">
              Our team looked at your sales background and put you forward for this role at {companyName(job)}. Read
              the details below, then create your free account to apply. Your application goes straight to the hiring
              team.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/apply/$jobId"
                params={{ jobId: job.id }}
                search={applySearch}
                className="inline-flex items-center rounded-full bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-purple-strong transition hover:-translate-y-0.5"
              >
                Apply to this role
              </Link>
              <Link
                to="/jobs/$jobId"
                params={{ jobId: job.id }}
                className="inline-flex items-center rounded-full border border-white/25 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white/80 transition hover:border-white/60 hover:text-white"
              >
                View the full advert
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-5 lg:grid-cols-[1.4fr_1fr] lg:items-start">
          <div className="rounded-[28px] border border-ink/10 bg-white/75 p-7 sm:p-9">
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">The role</p>
            <h2 className="mt-3 font-serif text-[22px] leading-[1.08] tracking-[-0.02em] text-ink">{job.title}</h2>
            <p className="mt-3 text-[13px] uppercase tracking-[0.14em] text-ink/50">
              {companyName(job)} · {job.location} · {job.setting} · {job.contract}
            </p>
            <dl className="mt-7 grid gap-4 sm:grid-cols-2">
              {[
                { k: "Pay", v: job.pay },
                { k: "Structure", v: job.payStructure },
                { k: "Seniority", v: job.seniority },
                { k: "Contract", v: job.contract },
              ].map((row) => (
                <div key={row.k} className="rounded-[18px] border border-ink/10 bg-cream px-5 py-4">
                  <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/45">{row.k}</dt>
                  <dd className="mt-1.5 text-[15px] font-semibold leading-[1.5] text-ink">{row.v}</dd>
                </div>
              ))}
            </dl>
            {job.advert ? (
              <p className="mt-7 whitespace-pre-line text-[17px] leading-[1.7] text-ink/70">{job.advert}</p>
            ) : null}
          </div>

          <div className="rounded-[28px] border border-purple-strong/15 bg-purple-soft p-7 sm:p-9 lg:sticky lg:top-24">
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-purple-strong">How to apply</p>
            <ol className="mt-5 grid gap-5">
              {[
                "Create your free CLOSDD account.",
                "Add your CV and record a 60-second pitch.",
                "Answer three short voice questions.",
                "We send it straight to the hiring team.",
                "Or let us apply for roles on your behalf — create your profile, record your pitch, and we'll handle the rest.",
              ].map((step, i) => (
                <li key={step} className="flex gap-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-purple-strong text-[13px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="text-[16px] leading-[1.55] text-ink/75">{step}</span>
                </li>
              ))}
            </ol>
            <Link to="/apply/$jobId" params={{ jobId: job.id }} search={applySearch} className={`${cta} mt-8 w-full justify-center`}>
              Create account and apply
            </Link>
            <Link to="/apply/$jobId" params={{ jobId: job.id }} search={applySearch} className={`${ctaLight} mt-3 w-full justify-center`}>
              I already have an account
            </Link>
            <p className="mt-5 text-[13px] leading-[1.6] text-ink/55">
              Free for candidates, always. Questions? Email info@closdd.com.
            </p>
          </div>
        </section>
        <div className="mt-10">
          <SeoFooter />
        </div>
      </main>
    </div>
  );
}
